/* eslint-disable react-hooks/exhaustive-deps */
import { TextField } from "@material-ui/core";
import { FastField, getIn } from "formik";
import PropTypes from "prop-types";
import React, { memo, useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";
import RequiredLabel from "./RequiredLabel";
import configDefaultForm from "./configDefault";

const GlobitsVNDCurrencyInputOnlyChange = (props) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component
        {...props}
        field={field}
        meta={meta}
        setFieldValue={form.setFieldValue}
      />
    )}
  </FastField>
);

const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const {
    isOnlyOnChange,
    decimalScale,
    onChange,
    onBlur,
    isDot,
    suffix,
    ...other
  } = props;

  const handleValueChange = (values) => {
    let roundedValue = values.floatValue;
    if (roundedValue !== undefined) {
      roundedValue =
        decimalScale === 0 ? Math.round(roundedValue) : values.value;
    }
    onChange({
      target: {
        name: props.name,
        value: roundedValue ?? "",
      },
    });
  };

  const handleOnlyOnchange = (e) => {
    if (onChange) {
      let inputValue = e.target.value;
      let floatValue = parseFloat(inputValue.replace(/,/g, ""));
      if (isNaN(floatValue)) {
        floatValue = "";
      } else if (decimalScale === 0) {
        floatValue = Math.round(floatValue);
      }
      onChange({
        target: {
          name: props.name,
          value: floatValue ?? "",
        },
      });
    }
  };

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onBlur={onBlur} // 🔁 Đảm bảo onBlur được forward để Formik đánh dấu touched
      {...(isOnlyOnChange
        ? { onChange: handleOnlyOnchange }
        : { onValueChange: handleValueChange })}
      thousandSeparator={isDot ? "." : ","}
      decimalSeparator={isDot ? "," : "."}
      valueIsNumericString
      suffix={suffix}
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  isDot: PropTypes.bool,
};

const Component = ({
  name,
  fullWidth = configDefaultForm.fullWidth,
  label,
  type = "text",
  debounceTime = 400,
  notDelay,
  field,
  meta,
  disabled,
  placeholder,
  isTextArea,
  minRowArea,
  required,
  requiredLabel,
  className = "",
  onChange,
  setFieldValue,
  readOnly,
  autoCapitalize = false,
  isDot = false,
  suffix = "₫",
  colorText,
  roundNumbers = 0,

  ...otherProps
}) => {
  const [value, setValue] = useState(field.value);
  const [t, setT] = useState(undefined);

  const displayValue = useMemo(() => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;

    if (Number.isInteger(num)) return num.toString();

    // Làm tròn theo roundNumbers
    const rounded = num.toFixed(roundNumbers);

    // Nếu sau khi làm tròn không có dấu chấm (là số nguyên), trả về nguyên
    if (!rounded.includes(".")) return rounded;

    // Nếu có dấu chấm, loại bỏ số 0 thập phân thừa
    return rounded.replace(/\.?0+$/, "");
  }, [value, roundNumbers]);

  useEffect(() => {
    if (field.value !== value) setValue(field.value ?? "");
  }, [field.value]);

  const handleChange = (e) => {
    let value = e.target.value;
    if (autoCapitalize) {
      if (value.charAt(0) === value.charAt(0).toLowerCase()) {
        value = value.replace(
          value.charAt(0),
          value.charAt(0).toLocaleUpperCase()
        );
      }
      value = value.replace(/\s\S/g, (s) => s.toLocaleUpperCase());
    }

    setValue(value);

    if (!notDelay) {
      if (t) clearTimeout(t);
      const update = onChange
        ? () => onChange(e)
        : () => setFieldValue(name, e.target.value || null);
      setT(setTimeout(update, debounceTime));
    } else {
      onChange ? onChange(e) : setFieldValue(name, e.target.value || null);
    }
  };

  const displayLabel = useMemo(
    () =>
      label ? (
        <RequiredLabel
          label={label}
          requiredLabel={required || requiredLabel}
        />
      ) : null,
    [label, required, requiredLabel]
  );

  return (
    <>
      {!fullWidth && displayLabel && (
        <label htmlFor={name}>{displayLabel}</label>
      )}
      <TextField
        multiline={isTextArea}
        id={name}
        name={name}
        value={displayValue ?? ""}
        fullWidth
        label={fullWidth && displayLabel ? displayLabel : null}
        onChange={handleChange}
        onBlur={field.onBlur} // ✅ Thêm onBlur ở đây
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        error={Boolean(meta?.touched && meta?.error)}
        helperText={meta?.touched && meta?.error ? meta.error : ""}
        InputProps={{
          readOnly: readOnly,
          inputComponent: NumericFormatCustom,
          inputProps: {
            isDot: isDot,
            ...otherProps,
          },
          style: {
            color: colorText,
          },
          endAdornment: suffix && (
            <span style={{ marginLeft: "8px", color: "#757575" }}>
              {suffix}
            </span>
          ),
        }}
        InputLabelProps={{
          htmlFor: name,
          shrink: true,
        }}
        className={`input-container ${className} ${
          readOnly ? "read-only" : ""
        } ${isTextArea ? "isTextArea" : ""}`}
        minRows={minRowArea}
      />
    </>
  );
};

const shouldComponentUpdate = (nextProps, currentProps) =>
  nextProps?.readOnly !== currentProps?.readOnly ||
  nextProps?.value !== currentProps?.value ||
  nextProps?.onChange !== currentProps?.onChange ||
  nextProps?.disabled !== currentProps?.disabled ||
  nextProps?.name !== currentProps?.name ||
  Object.assign(nextProps).length !== Object.assign(currentProps).length ||
  getIn(nextProps.formik.values, currentProps.name) !==
    getIn(currentProps.formik.values, currentProps.name) ||
  getIn(nextProps.formik.errors, currentProps.name) !==
    getIn(currentProps.formik.errors, currentProps.name) ||
  getIn(nextProps.formik.touched, currentProps.name) !==
    getIn(currentProps.formik.touched, currentProps.name);

export default memo(GlobitsVNDCurrencyInputOnlyChange);
